import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {from, Observable, switchMap} from 'rxjs';
import {SecurityConfigService} from '@twpub/shared/services/security-config.service';
import {KeycloakService} from 'keycloak-angular';

/**
 * HTTP interceptor that adds the JWT token to outgoing requests.
 * - For external authentication: adds the stored external token
 * - For Keycloak authentication: adds the Keycloak bearer token
 * The token is added as a Bearer token in the Authorization header.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private securityService: SecurityConfigService,
    private keycloakService: KeycloakService
  ) {
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip auth for the security config bootstrap request to avoid circular dependency:
    // initializeAuthentication fetches security/config, but this interceptor calls
    // isExternalAuth() which requires the config to already be loaded.
    if (req.url.includes('security/config')) {
      return next.handle(req);
    }

    // Check if Keycloak is authenticated directly - this handles the case where
    // config might not be initialized yet, but Keycloak is already authenticated
    const keycloak = this.keycloakService.getKeycloakInstance();
    const isKeycloakAuthenticated = keycloak?.authenticated;

    // Handle Keycloak authentication (when in keycloakAdminMode, Keycloak is authenticated,
    // or using Keycloak as primary auth without external auth)
    if (isKeycloakAuthenticated || this.securityService.isKeycloakAdminMode()) {
      return this.handleKeycloakAuth(req, next);
    }

    // Handle external authentication
    if (this.securityService.isExternalAuth()) {
      const token = this.securityService.getToken();
      if (token) {
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });
        return next.handle(authReq);
      }
    }

    return next.handle(req);
  }

  private handleKeycloakAuth(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Get token from Keycloak (may trigger token refresh if needed)
    return from(this.getKeycloakToken()).pipe(
      switchMap(token => {
        if (token) {
          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });
          return next.handle(authReq);
        }
        return next.handle(req);
      })
    );
  }

  private async getKeycloakToken(): Promise<string | undefined> {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      if (keycloak?.authenticated) {
        // Update token if it will expire within 30 seconds
        await keycloak.updateToken(30);
        return keycloak.token;
      }
    } catch (error) {
      console.error('Failed to get Keycloak token:', error);
    }
    return undefined;
  }
}
